/**
 * Lightweight analytics + error-monitoring bootstrapper.
 *
 * No-ops if the corresponding env var is missing — so it's safe to ship.
 * Activate by setting REACT_APP_GA4_ID and/or REACT_APP_SENTRY_DSN at build time.
 */

// ---------- Google Analytics 4 ----------
export const initGA4 = () => {
  const id = process.env.REACT_APP_GA4_ID;
  if (!id) return;
  // Avoid double init in HMR
  if (window.__growthriq_ga4_loaded) return;
  window.__growthriq_ga4_loaded = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', id, {
    anonymize_ip: true,
    send_page_view: true,
  });
};

// Manual event helper — call from anywhere
export const trackEvent = (eventName, params = {}) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }
  // Forward to PostHog if available (already installed in index.html)
  if (window.posthog && typeof window.posthog.capture === 'function') {
    try { window.posthog.capture(eventName, params); } catch (_) { /* noop */ }
  }
};

// ---------- Sentry (browser) ----------
// Loaded lazily to avoid adding ~80KB to the main bundle when DSN is not set.
export const initSentry = async () => {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return;
  if (window.__growthriq_sentry_loaded) return;
  window.__growthriq_sentry_loaded = true;

  try {
    const Sentry = await import('@sentry/react');
    Sentry.init({
      dsn,
      environment: process.env.NODE_ENV,
      tracesSampleRate: Number(process.env.REACT_APP_SENTRY_TRACES_RATE || 0.1),
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      release: process.env.REACT_APP_RELEASE || 'unknown',
    });
  } catch (e) {
    // Silently skip — Sentry SDK is optional; install with `yarn add @sentry/react` when ready.
    console.warn('[GrowthRIQ] Sentry SDK not installed; skipping. Run: yarn add @sentry/react');
  }
};

export const initObservability = () => {
  initGA4();
  initSentry();
};
